// Face enrollment: three stills (straight / slightly left / slightly right),
// each with a thumbnail and Retake, then POST /attendance/face/enroll. A 422
// shows the server's message and keeps the shots so the person can retake the
// bad one and save again. Shots are dropped the moment the window closes.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ScanFace, Camera, RotateCcw, Check, Save } from "lucide-react";
import RemotePunchDialog, { dialogColors } from "../RemotePunch/RemotePunchDialog";
import FaceCameraView from "./FaceCameraView";
import { useFaceCapture, CAMERA_STATUS } from "../../pages/(User)attendance_page/hooks/useFaceCapture";
import { faceApi } from "./faceApi";
import { faceText, fill, describeFaceError } from "./faceText";

const SHOTS = ["straight", "left", "right"];

export default function FaceEnrolModal({ open, onClose, onEnrolled, isDarkMode, language }) {
  const t = faceText(language);
  const c = dialogColors(isDarkMode);
  const cam = useFaceCapture();

  // Each shot keeps its Blob and a preview URL; the URL is revoked as soon as
  // the shot is retaken, the window closes or the component unmounts.
  const [shots, setShots] = useState([null, null, null]); // { blob, url } | null
  const [current, setCurrent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);     // { title, message, code, server }
  const [flash, setFlash] = useState(false);
  const shotsRef = useRef(shots);
  shotsRef.current = shots;

  const revokeAll = (list) => list.forEach((s) => s?.url && URL.revokeObjectURL(s.url));

  const reset = useCallback(() => {
    revokeAll(shotsRef.current);
    setShots([null, null, null]);
    setCurrent(0);
    setError(null);
    setSaving(false);
  }, []);

  useEffect(() => {
    if (open) { cam.start(); } else { cam.stop(); reset(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => () => revokeAll(shotsRef.current), []);

  const allTaken = shots.every(Boolean);
  const nextEmpty = shots.findIndex((s) => !s);

  const take = async () => {
    try {
      const blob = await cam.captureFrame();
      setFlash(true); setTimeout(() => setFlash(false), 140);
      const shot = { blob, url: URL.createObjectURL(blob) };
      const next = shots.map((s, i) => (i === current ? shot : s));
      if (shots[current]?.url) URL.revokeObjectURL(shots[current].url);
      setShots(next);
      setError(null);
      const after = next.findIndex((s) => !s);
      if (after !== -1) setCurrent(after);
    } catch (err) {
      setError({ title: t.failedTitle, message: t.cameraError });
    }
  };

  const retake = (i) => {
    setShots((prev) => { const n = [...prev]; if (n[i]?.url) URL.revokeObjectURL(n[i].url); n[i] = null; return n; });
    setCurrent(i);
    setError(null);
  };

  const save = async () => {
    if (!allTaken || saving) return;
    setSaving(true);
    setError(null);
    try {
      await faceApi.enroll(shots.map((s) => s.blob));
      reset();
      await onEnrolled?.();
    } catch (err) {
      const d = describeFaceError(err, language);
      setError({ ...d, title: t.enrolFailed, rejected: err?.status === 422 });
    } finally {
      setSaving(false);
    }
  };

  const cameraErrorLabel =
    cam.status === CAMERA_STATUS.DENIED ? t.cameraDenied
      : cam.status === CAMERA_STATUS.UNAVAILABLE ? t.cameraUnavailable
        : t.cameraError;

  const btn = (kind, disabled) => ({
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
    flex: "1 1 160px", padding: "12px 18px", borderRadius: 12, fontSize: 15, fontWeight: 800, fontFamily: "inherit",
    border: kind === "primary" ? "none" : `1px solid ${c.border}`,
    background: kind === "primary" ? c.primary : kind === "success" ? c.success : "transparent",
    color: kind === "ghost" ? c.text : "#fff",
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
    boxShadow: kind !== "ghost" ? `0 4px 14px ${kind === "success" ? c.success : c.primary}44` : "none",
  });

  return (
    <RemotePunchDialog
      open={open}
      onClose={saving ? undefined : onClose}
      title={t.enrolTitle}
      subtitle={t.enrolSubtitle}
      icon={ScanFace}
      dark={isDarkMode}
      language={language}
      maxWidth={560}
      closeLabel={t.close}
      footer={
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {!allTaken ? (
            <button type="button" onClick={take} disabled={!cam.isReady || saving} style={btn("primary", !cam.isReady || saving)} data-testid="face-enrol-take">
              <Camera size={18} /> {t.takePhoto}
            </button>
          ) : (
            <button type="button" onClick={save} disabled={saving} style={btn("success", saving)} data-testid="face-enrol-save">
              <Save size={18} /> {saving ? t.submitting : t.submitEnrol}
            </button>
          )}
          <button type="button" onClick={onClose} disabled={saving} style={btn("ghost", saving)}>
            {t.cancel}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ textAlign: "center", fontWeight: 800, fontSize: 14, color: c.muted }}>
          {fill(t.shotOf, { n: current + 1 })}
        </div>

        <FaceCameraView
          videoRef={cam.videoRef}
          previewStyle={cam.previewStyle}
          status={cam.status}
          prompt={!allTaken ? t.enrolPrompts[SHOTS[current]] : null}
          busyLabel={saving ? t.submitting : null}
          errorLabel={cameraErrorLabel}
          onRetryCamera={cam.start}
          retryLabel={t.retry}
          dark={isDarkMode}
          guideColor={allTaken ? c.success : c.primary}
        >
          {flash && <div style={{ position: "absolute", inset: 0, background: "#fff", opacity: 0.7 }} />}
        </FaceCameraView>

        {/* Thumbnails — mirrored like the preview so they look like what the person saw. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 10 }}>
          {SHOTS.map((key, i) => {
            const taken = Boolean(shots[i]);
            const active = i === current && !allTaken;
            return (
              <div key={key} style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch" }}>
                <button
                  type="button"
                  onClick={() => (taken ? retake(i) : setCurrent(i))}
                  disabled={saving}
                  title={taken ? t.retake : t.enrolPrompts[key]}
                  style={{
                    position: "relative", aspectRatio: "4 / 3", borderRadius: 12, overflow: "hidden", padding: 0,
                    border: `2px solid ${active ? c.primary : taken ? c.success : c.border}`,
                    background: c.soft, cursor: saving ? "not-allowed" : "pointer",
                  }}
                  data-testid={`face-enrol-thumb-${i}`}
                >
                  {taken ? (
                    <img src={shots[i].url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)", display: "block" }} />
                  ) : (
                    <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: c.muted }}>
                      <Camera size={22} />
                    </div>
                  )}
                  {taken && (
                    <div style={{ position: "absolute", top: 6, insetInlineEnd: 6, width: 22, height: 22, borderRadius: "50%", background: c.success, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Check size={14} />
                    </div>
                  )}
                </button>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: c.muted, textAlign: "center", lineHeight: 1.4 }}>
                  {t.enrolPrompts[key]}
                </div>
                {taken && (
                  <button type="button" onClick={() => retake(i)} disabled={saving} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4, padding: "6px 8px", borderRadius: 8, border: `1px solid ${c.border}`, background: "transparent", color: c.text, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    <RotateCcw size={12} /> {t.retake}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div role="alert" style={{ padding: "12px 14px", borderRadius: 12, background: `${c.danger}14`, border: `1px solid ${c.danger}55`, color: c.text, fontSize: 13.5, lineHeight: 1.7 }}>
            <div style={{ fontWeight: 800, color: c.danger }}>{error.title}</div>
            <div>{error.message}</div>
            {error.server && <div style={{ color: c.muted, fontSize: 12.5 }}>{error.server}</div>}
            {error.rejected && <div style={{ marginTop: 4, fontWeight: 700 }}>{t.enrolRejectedHint}</div>}
          </div>
        )}
        {nextEmpty === -1 && !error && !saving && (
          <div style={{ textAlign: "center", fontSize: 13, color: c.muted }}>{t.submitEnrol} →</div>
        )}
      </div>
    </RemotePunchDialog>
  );
}
